import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import DataTable from '../components/DataTable';
import FilamentForm from '../components/FilamentForm';
import ConfirmationModal from '../components/ConfirmationModal';
import AdminBarcodeModal from '../components/AdminBarcodeModal';
import { useToast } from '../hooks/useToast';
import { useDebounce } from '../hooks/useDebounce';

const API_PREFIX = '/internal/filaments/global';

export default function AdminFilaments() {
  const [filaments, setFilaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingFilament, setEditingFilament] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showBarcodeModal, setShowBarcodeModal] = useState(false);
  const [barcode, setBarcode] = useState(null);
  const [barcodeResult, setBarcodeResult] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [materialExtras, setMaterialExtras] = useState([]);
  const [brands, setBrands] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    material: '',
    materialExtra: '',
    brand: '',
  });

  const barcodeInput = useRef('');
  const barcodeTimeout = useRef(null);

  const { showToast } = useToast();

  const debouncedSearch = useDebounce(filters.search);

  const readBarcode = (e) => {
    if (barcodeTimeout.current) {
      clearTimeout(barcodeTimeout.current);
      barcodeTimeout.current = null;
    }
    if (e.code === 'Enter') {
      if (barcodeInput.current) {
        setBarcode(barcodeInput.current);
      }
      barcodeInput.current = '';
      return;
    }
    if (e.key !== 'Shift') {
      barcodeInput.current += e.key;
    }
    barcodeTimeout.current = setInterval(() => (barcodeInput.current = ''), 200);
  };

  const fetchFilaments = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('ownership', 'system');

      if (debouncedSearch) {
        params.append('q', debouncedSearch);
      }
      if (filters.material) {
        params.append('material', filters.material);
      }
      if (filters.brand) {
        params.append('brand', filters.brand);
      }
      if (filters.materialExtra) {
        params.append('extra', filters.materialExtra);
      }

      const response = await api.get(`/auth/filaments?${params.toString()}`);
      setFilaments(response.data.data || []);
    } catch (err) {
      setError('Failed to fetch system filaments');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, filters.material, filters.brand, filters.materialExtra]);

  useEffect(() => {
    fetchMaterials();
    fetchMaterialExtras();
    fetchBrands();
  }, []);

  useEffect(() => {
    if (showForm) return;
    window.addEventListener('keydown', readBarcode);
    return () => window.removeEventListener('keydown', readBarcode);
  }, [showForm]);

  useEffect(() => {
    fetchFilaments();
  }, [fetchFilaments]);

  useEffect(() => {
    if (!barcode) return;
    async function lookupBarcode() {
      try {
        const response = await api.get(`${API_PREFIX}/barcode/${barcode}`);
        const filament = response.data?.data;
        if (filament) {
          setBarcodeResult({ type: 'match', data: filament });
        } else {
          setBarcodeResult({ type: 'not_found' });
        }
        setShowBarcodeModal(true);
      } catch (err) {
        if (err.response?.status === 404) {
          setBarcodeResult({ type: 'not_found' });
          setShowBarcodeModal(true);
        } else {
          console.error('Barcode lookup error:', err);
        }
      }
    }
    lookupBarcode();
  }, [barcode]);

  const fetchMaterials = async () => {
    try {
      const response = await api.get('/auth/materials?ownership=system');
      setMaterials(response.data.data || []);
    } catch (err) {
      console.error('Failed to fetch materials:', err);
    }
  };

  const fetchMaterialExtras = async () => {
    try {
      const response = await api.get('/auth/material-extras?ownership=system');
      setMaterialExtras(response.data.data || []);
    } catch (err) {
      console.error('Failed to fetch material extras:', err);
    }
  };

  const fetchBrands = async () => {
    try {
      const response = await api.get('/auth/filament-brands?ownership=system');
      setBrands(response.data.data || []);
    } catch (err) {
      console.error('Failed to fetch brands:', err);
    }
  };

  const closeBarcodeModal = () => {
    setShowBarcodeModal(false);
    setBarcode(null);
    setBarcodeResult(null);
  };

  const handleBarcodeEdit = (filamentData) => {
    setShowBarcodeModal(false);
    setEditingFilament(filamentData);
    setShowForm(true);
  };

  const handleBarcodeAssign = async (data) => {
    const { filament, barCodes } = data;
    await api.patch(`${API_PREFIX}/${filament}`, { barCodes });
    fetchFilaments();
    closeBarcodeModal();
    showToast('Barcode assigned successfully!', 'success');
  };

  const handleBarcodeCreateNew = () => {
    setShowBarcodeModal(false);
    setEditingFilament(null);
    setShowForm(true);
  };

  const handleAdd = () => {
    setEditingFilament(null);
    setShowForm(true);
  };

  const handleEdit = (filament) => {
    setEditingFilament(filament);
    setShowForm(true);
  };

  const handleDuplicate = async (filament) => {
    try {
      const response = await api.post(`${API_PREFIX}/${filament._id}/duplicate`);
      const created = response.data?.data;
      fetchFilaments();
      setEditingFilament(created);
      setShowForm(true);
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to duplicate filament', 'error');
    }
  };

  const handleDelete = (filament) => {
    setDeleteTarget(filament);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`${API_PREFIX}/${deleteTarget._id}`);
      showToast(`Filament "${deleteTarget.name || deleteTarget.fullName}" deactivated`, 'success');
      setDeleteTarget(null);
      fetchFilaments();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to delete filament', 'error');
    }
  };

  const handleSave = async (data) => {
    const isEditing = !!editingFilament;
    if (isEditing) {
      await api.patch(`${API_PREFIX}/${editingFilament._id}`, data);
    } else {
      await api.post(API_PREFIX, data);
    }
    setShowForm(false);
    setEditingFilament(null);
    setBarcode(null);
    fetchFilaments();
    showToast(
      isEditing ? 'System filament updated successfully!' : 'System filament created successfully!',
      'success'
    );

  };

  const columns = [
    { key: 'fullName', label: 'Name', sortable: true },
    {
      key: 'brand',
      label: 'Brand',
      sortable: true,
      render: (val) => typeof val === 'object' ? val?.name : val
    },
    {
      key: 'material',
      label: 'Material',
      sortable: true,
      render: (val) => typeof val === 'object' ? `${val?.name} (${val?.key})` : val
    },
    {
      key: 'materialExtra',
      label: 'Extra',
      render: (val) => {
        if (val === null) return '-'
        const names = Object.keys(val.name).map((item) => `<strong>${item}:</strong> ${val.name[item]}`)
        return `<strong>key:</strong> ${val.key}<br/>${names.join('<br/>')}`
      }
    },
    {
      key: 'color',
      label: 'Color',
      render: (val) => val ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ backgroundColor: `#${val.hex}`, display: 'inline-block', width: '1rem', height: '1rem', border: '1px solid #ccc' }}></span>
          {val.name}
        </div>
      ) : 'N/A'
    },
    {
      key: 'diameter',
      label: 'Diameter',
      render: (val) => val ? `${val}mm` : '—'
    },
    {
      key: 'weight',
      label: 'Weight',
      render: (val) => val ? `${val}g` : '—'
    },
    {
      key: 'costKg',
      label: 'Cost/kg',
      render: (val) => val ? `€${val.toFixed(2)}` : '—'
    },
    {
      key: 'barCodes',
      label: 'Barcodes',
      render: (val) => val?.length ? val.length : '—'
    },
  ];

  const renderFilters = () => (
    <div className="filters" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="Search by name, brand, or color..."
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          style={{ flex: '1 1 300px' }}
        />
        <select
          value={filters.material}
          onChange={(e) => setFilters({ ...filters, material: e.target.value })}
          style={{ flex: '1 1 150px' }}
        >
          <option value="">All Materials</option>
          {materials.map((m) => (
            <option key={m._id} value={m._id}>
              {m.name} ({m.key})
            </option>
          ))}
        </select>
        <select
          value={filters.materialExtra}
          onChange={(e) => setFilters({ ...filters, materialExtra: e.target.value })}
          style={{ flex: '1 1 150px' }}
        >
          <option value="">All Extras</option>
          {materialExtras.map((m) => (
            <option key={m._id} value={m._id}>
              {m.name?.default} ({m.key})
            </option>
          ))}
        </select>
        <select
          value={filters.brand}
          onChange={(e) => setFilters({ ...filters, brand: e.target.value })}
          style={{ flex: '1 1 150px' }}
        >
          <option value="">All Brands</option>
          {brands.map((b) => (
            <option key={b._id} value={b._id}>
              {b.name}
            </option>
          ))}
        </select>
      </div>
    </div>
  );

  if (loading && filaments.length === 0) return <div>Loading...</div>;
  if (error && filaments.length === 0) return <div className="error">{error}</div>;

  return (
    <div>
      {showForm ? (
        <FilamentForm
          filament={editingFilament}
          barcodeInput={barcode}
          ownership="system"
          onSave={handleSave}
          onCancel={() => {
            setShowForm(false);
            setEditingFilament(null);
            setBarcode(null);
          }}
        />
      ) : (
        <DataTable
          title="System Filament Presets"
          data={filaments}
          columns={columns}
          allowHTML={true}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onDuplicate={handleDuplicate}
          onAdd={handleAdd}
          renderFilters={renderFilters}
        />
      )}

      {showBarcodeModal && (
        <AdminBarcodeModal
          barcode={barcode}
          barcodeResult={barcodeResult}
          onEdit={handleBarcodeEdit}
          onAssign={handleBarcodeAssign}
          onCreateNew={handleBarcodeCreateNew}
          onCancel={closeBarcodeModal}
        />
      )}

      {deleteTarget && (
        <ConfirmationModal
          title="Delete System Filament"
          message={`Are you sure you want to deactivate "${deleteTarget.name || deleteTarget.fullName}"? Operators who already duplicated it will not be affected.`}
          onConfirm={confirmDelete}
          onCancel={() => setDeleteTarget(null)}
          confirmText="Deactivate"
        />
      )}
    </div>
  );
}
